import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ResponsiveContainer, AreaChart, Area } from "recharts";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";
import { useRealtime } from "../lib/useRealtime";
import { useToast } from "../lib/toast";
import type { components } from "../lib/api-types";
import ExecutiveExportButton from "../components/ExecutiveExportButton";
import ConceptFlow from "../components/ConceptFlow";
import Hint from "../components/Hint";
import KpiCard, { type CockpitKpi } from "../components/KpiCard";

type Kpi = CockpitKpi;
type Anom = { id: string; severity: "info"|"warning"|"critical"; title: string; message: string; kpi_id?: string; detected_at: string };
type Act = { id: string; type: string; title: string; summary: string; ts: string; agent?: string };
type DataTrustGateStatus = components["schemas"]["DataTrustGateStatus"];
type TenantIsolationStatus = components["schemas"]["TenantIsolationStatus"];
type AuditStatus = { files: Array<Record<string, any>> } & Record<string, any>; // generierter Typ fehlt in openapi-Types
type AuditRecord = components["schemas"]["AuditRecord"];
type AgentApprovalQueueReport = components["schemas"]["AgentApprovalQueueReport"];
type Summary = {
  tenant_id: string; generated_at: string; has_data: boolean; onboarding_progress: number;
  trust_gate?: DataTrustGateStatus;
  kpis: Kpi[]; anomalies: Anom[]; activity: Act[];
  open_candidates: number; pending_briefings: number;
};
type ProactiveSuggestion = {
  id: string;
  priority: "high" | "medium" | "low";
  title: string;
  message: string;
  action_label: string;
  route: string;
  advisor_mode: string;
  advisor_persona: string;
};
type ProactiveResponse = {
  tenant_id: string;
  generated_at: string;
  model_route: string;
  evidence_refs: string[];
  suggestions: ProactiveSuggestion[];
};

function fmt(v: number, unit: string): string {
  if (unit === "EUR") return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
  if (unit === "%") return v.toFixed(1) + " %";
  if (unit === "score") return (v >= 0 ? "+" : "") + Math.round(v);
  if (unit === "days") return Math.round(v) + " T";
  if (unit === "ratio") return "1 : " + v.toFixed(1);
  return v.toString();
}

export default function CommandCenterPage() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const { connected, events } = useRealtime();
  const toast = useToast();

  const sum = useQuery({
    queryKey: ["cockpit-summary", effectiveTenantId],
    queryFn: () => api.get<Summary>("/cockpit/summary"),
    refetchInterval: 30_000,
  });
  const proactive = useQuery({
    queryKey: ["advisor-proactive", effectiveTenantId],
    queryFn: () => api.get<ProactiveResponse>("/advisor/proactive"),
    refetchInterval: 25_000,
  });
  const isolation = useQuery({
    queryKey: ["command-center-tenant-isolation", effectiveTenantId],
    queryFn: () => api.get<TenantIsolationStatus>("/tenant-isolation/status"),
    refetchInterval: 30_000,
  });
  const auditStatus = useQuery({
    queryKey: ["command-center-audit-status", effectiveTenantId],
    queryFn: () => api.get<AuditStatus>("/audit/status"),
    refetchInterval: 30_000,
  });
  const approvalQueue = useQuery({
    queryKey: ["command-center-approval-queue", effectiveTenantId],
    queryFn: () => api.get<AgentApprovalQueueReport>("/agent-runs/approval-queue"),
    refetchInterval: 15_000,
  });
  const evidenceLedger = useQuery({
    queryKey: ["command-center-evidence-ledger", effectiveTenantId],
    queryFn: async () => {
      const [runs, replays, evidence] = await Promise.all([
        api.get<AuditRecord[]>("/audit/records?kind=agent_runs&limit=3"),
        api.get<AuditRecord[]>("/audit/records?kind=agent_replays&limit=2"),
        api.get<AuditRecord[]>("/audit/records?kind=evidence&limit=2"),
      ]);
      return [...runs, ...replays, ...evidence]
        .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
        .slice(0, 6);
    },
    refetchInterval: 30_000,
  });

  // Replay incoming events as toasts
  useEffect(() => {
    if (events.length > 0) {
      const e = events[0];
      if (e.type === "agent_run" && e.payload) {
        toast.push({ kind: "info", title: `Agent: ${e.payload.agent}`, body: e.payload.summary || e.payload.status });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events.length]);

  const recentActivity = useMemo<Act[]>(() => {
    const live: Act[] = events.filter((e) => e.type === "agent_run" && e.payload).map((e) => ({
      id: e.id, type: "agent_run", title: `Agent: ${e.payload.agent}`,
      summary: e.payload.summary || "",
      ts: new Date(e.ts * 1000).toISOString(), agent: e.payload.agent,
    }));
    const base = sum.data?.activity || [];
    return [...live, ...base].slice(0, 10);
  }, [events, sum.data]);

  if (sum.isLoading) return <SkeletonCenter />;
  if (sum.error || !sum.data) return <p style={{ padding: 24 }} className="muted">Cockpit-Summary nicht verfügbar.</p>;
  const d = sum.data;
  const isDemo = effectiveTenantId === "tnt_demo";
  const hasTrustedData = d.has_data && !isDemo;
  const trustGate = d.trust_gate;
  const nextTrustAction = trustGate?.next_actions?.[0];
  const topAnomaly = d.anomalies[0];
  const proactiveSuggestions = proactive.data?.suggestions ?? [];

  return (
    <section className="cmd-center">
      <header className="cmd-hero">
        <div>
          <p className="hero-eyebrow">Command Center · {isDemo ? "Sandbox-Demo" : effectiveTenantId}</p>
          <h1>Entscheidungslage</h1>
          <p className="hero-sub">Was ist passiert, warum, was tun, wer verantwortet es und welche Evidenz gibt es. Echtzeit {connected ? <span className="dot-live">● verbunden</span> : <span className="dot-off">○ offline</span>} · Stand {new Date(d.generated_at).toLocaleTimeString("de-DE")}</p>
        </div>
        <div className="hero-stats">
          <div className="stat-pill"><strong>{d.kpis.length}</strong> KPIs</div>
          <div className="stat-pill"><strong>{d.open_candidates}</strong> Kandidaten</div>
          <div className="stat-pill"><strong>{d.pending_briefings}</strong> Briefings pending</div>
          <ExecutiveExportButton
            compact
            label="Export"
            audience="cfo"
            scope={["command-center", "kpi-summary", "evidence"]}
          />
          <div className="stat-pill cmdk-hint" title="Cmd+K">⌘K · Befehle</div>
        </div>
      </header>

      <ConceptFlow />

      {isDemo && (
        <section className="trust-banner demo">
          <strong>Sandbox-Demo sichtbar aktiv.</strong>
          <span>Alle Werte, Agent-Runs und Briefings sind Beispieldaten. Für echte Tenants bleiben KPI-Zahlen gesperrt, bis Onboarding, Datenquellen und Quality Gate abgeschlossen sind.</span>
        </section>
      )}

      {!hasTrustedData && !isDemo && (
        <section className="trust-banner blocked">
          <strong>Datenvertrauen fehlt.</strong>
          <span>{nextTrustAction ?? "Dieser Tenant zeigt noch keine KPI-Werte. Starten Sie mit der geführten Einführung und schließen Sie Quellen an, bevor das Command Center Zahlen freigibt."}</span>
          <Link to="/app/onboarding" className="btn primary small">Einführung starten</Link>
        </section>
      )}

      <DataTrustGatePanel gate={trustGate} />

      <ActionGovernancePanel
        queue={approvalQueue.data}
        loading={approvalQueue.isLoading}
        error={approvalQueue.error}
      />

      <ReleaseReadinessPanel
        trustGate={trustGate}
        isolation={isolation.data}
        isolationLoading={isolation.isLoading}
        isolationError={isolation.error}
        audit={auditStatus.data}
        auditLoading={auditStatus.isLoading}
        auditError={auditStatus.error}
        isDemo={isDemo}
      />

      <EvidenceLedgerPanel
        records={evidenceLedger.data ?? []}
        loading={evidenceLedger.isLoading}
        error={evidenceLedger.error}
      />

      {d.onboarding_progress < 100 && (
        <section className="cc-onboarding">
          <div>
            <strong>Onboarding {d.onboarding_progress} %</strong>
            <p className="muted small">Sobald 100 % erreicht sind, schalten echte KPIs frei.</p>
          </div>
          <div className="onb-track"><div className="onb-fill" style={{ width: `${d.onboarding_progress}%` }} /></div>
          <Link to="/app/onboarding" className="btn primary">Weiter</Link>
        </section>
      )}

      <ProductJourneyPanel
        onboardingProgress={d.onboarding_progress}
        trustGate={trustGate}
        openCandidates={d.open_candidates}
        pendingBriefings={d.pending_briefings}
        isDemo={isDemo}
        hasTrustedData={hasTrustedData}
      />

      {proactiveSuggestions.length > 0 && (
        <section className="proactive-panel">
          <div className="section-head">
            <h2>Proaktiver KI-Berater</h2>
            <span className="muted small">{proactive.data?.model_route ?? "local-first"} · {proactiveSuggestions.length} Hinweise</span>
          </div>
          <div className="proactive-grid">
            {proactiveSuggestions.map((item) => (
              <article key={item.id} className={`proactive-card priority-${item.priority}`}>
                <div>
                  <span className="proactive-priority">{item.priority}</span>
                  <h3>{item.title}</h3>
                  <p>{item.message}</p>
                </div>
                <Link to={item.route} className="btn small">
                  {item.action_label}
                </Link>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="decision-grid">
        <article>
          <span>Was ist passiert?</span>
          <strong>{topAnomaly ? topAnomaly.title : isDemo ? "Demo-Anomalien im Beispieltenant" : "Noch kein produktiver Datenstand"}</strong>
          <p>{topAnomaly?.message ?? "Nach dem Quality Gate werden Top-Mover, Anomalien und Statuswechsel hier zuerst angezeigt."}</p>
        </article>
        <article>
          <span>Warum?</span>
          <strong>{topAnomaly ? "RCA-Agent bereit" : "Ursachenanalyse wartet"}</strong>
          <p>{topAnomaly ? "Öffnen Sie den Driver Tree oder fragen Sie den KI-Berater mit vollständigem Kontext." : "Ohne geprüfte Quellen erzeugen wir keine Scheinerklärung."}</p>
        </article>
        <article>
          <span>Was tun?</span>
          <strong>{d.open_candidates > 0 ? `${d.open_candidates} KPI-Kandidaten reviewen` : "Nächster Setup-Schritt"}</strong>
          <p>{d.open_candidates > 0 ? "Human-in-the-loop Review entscheidet, welche Kennzahlen in produktive Steuerung gehen." : "Geführte Einführung, Datenquellen und Quality Check zuerst."}</p>
        </article>
        <article>
          <span>Wer verantwortet es?</span>
          <strong>{isDemo ? "CFO Demo Owner" : "Owner nach Onboarding"}</strong>
          <p>Jede Maßnahme braucht Rolle, Verantwortliche und Eskalationspfad, bevor ein Agent Workflow anstößt.</p>
        </article>
        <article>
          <span>Welche Evidenz?</span>
          <strong>{recentActivity.length} Agent-/Audit-Ereignisse</strong>
          <p>Jede Empfehlung muss auf Quelle, Trace und Evidence-Bundle verweisen.</p>
        </article>
      </section>

      <section className={`cc-kpis ${!hasTrustedData && !isDemo ? "locked" : ""}`}>
        <div className="section-head">
          <h2>Kennzahlen<Hint term="kpi" /></h2>
          <span className="muted small">
            {d.kpis.filter((k) => k.value_state !== "placeholder").length} von {d.kpis.length} mit echtem Ist-Wert
          </span>
        </div>
        {!hasTrustedData && !isDemo && (
          <div className="locked-state">
            <strong>KPI-Anzeige gesperrt</strong>
            <p>Quality Score und Datenmapping fehlen. Das ist Absicht: keine erfundenen Kennzahlen für neue Tenants.</p>
            <Link to="/app/sources" className="btn primary small">Datenquellen prüfen</Link>
          </div>
        )}
        <div className="kpi-grid">
          {(hasTrustedData || isDemo ? d.kpis : []).map((k) => <KpiCard key={k.id} k={k} />)}
        </div>
      </section>

      <div className="cc-grid">
        <section className="cc-anomalies">
          <div className="section-head"><h2>Anomalien</h2><span className="muted small">auto-detected</span></div>
          {d.anomalies.length === 0 && <p className="muted">Keine Auffälligkeiten.</p>}
          <ul className="anom-list">
            {d.anomalies.map((a) => (
              <li key={a.id} className={`anom-card sev-${a.severity}`}>
                <div className="anom-head">
                  <span className={`anom-sev sev-${a.severity}`}>{a.severity}</span>
                  <strong>{a.title}</strong>
                </div>
                <p>{a.message}</p>
                <div className="anom-actions">
                  {a.kpi_id && <Link to={`/app/trees/${a.kpi_id}`} className="btn ghost small">Driver-Tree</Link>}
                  <button className="btn small" onClick={async () => {
                    try {
                      const ex = await api.get<{suggested_message:string; suggested_mode:string; suggested_persona:string}>(`/anomalies/${a.id}/explain`);
                      const q = new URLSearchParams({ msg: ex.suggested_message, mode: ex.suggested_mode, persona: ex.suggested_persona });
                      window.location.href = `/app/advisor?${q.toString()}`;
                    } catch (e) { console.error(e); }
                  }}>KI-Berater fragen</button>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="cc-activity">
          <div className="section-head"><h2>Live-Aktivität</h2><span className="muted small">{connected ? "● live" : "○"} · {recentActivity.length}</span></div>
          {recentActivity.length === 0 && <p className="muted">Noch keine Aktivität.</p>}
          <ul className="act-list">
            {recentActivity.slice(0, 8).map((a) => (
              <li key={a.id} className="act-item">
                <div className="act-dot" />
                <div className="act-body">
                  <strong>{a.title}</strong>
                  <p className="muted small">{a.summary || a.type}</p>
                </div>
                <time className="muted small">{new Date(a.ts).toLocaleTimeString("de-DE")}</time>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="cc-quick">
        <div className="section-head"><h2>Schnellzugriffe</h2><span className="muted small">⌘K für alle Befehle</span></div>
        <div className="quick-grid">
          <Link to="/app/onboarding" className="quick-card">
            <span className="quick-icon">01</span>
            <strong>Geführte Einführung</strong>
            <p className="muted small">Branche, Ziele, Pain Points und Datenlage zuerst</p>
          </Link>
          <Link to="/app/potential" className="quick-card">
            <span className="quick-icon">02</span>
            <strong>KI-Potenzialanalyse</strong>
            <p className="muted small">Reifegrad, Quick Wins und KPI-Vorschlag</p>
          </Link>
          <Link to="/app/process-landscape" className="quick-card">
            <span className="quick-icon">12</span>
            <strong>Prozesslandschaft</strong>
            <p className="muted small">59 Teilprozesse · 26 Agenten · proaktive Trigger</p>
          </Link>
          <Link to="/app/advisor" className="quick-card">
            <span className="quick-icon">AI</span>
            <strong>KI-Berater starten</strong>
            <p className="muted small">4 Modi × 12 Personas · Audit-getrackt</p>
          </Link>
          <Link to="/app/sources" className="quick-card">
            <span className="quick-icon">DQ</span>
            <strong>Datenquelle anschließen</strong>
            <p className="muted small">CSV · REST · Crawl4AI · Nimble</p>
          </Link>
          <Link to="/app/roi" className="quick-card">
            <span className="quick-icon">ROI</span>
            <strong>Monte-Carlo simulieren</strong>
            <p className="muted small">P10/P50/P90 statt Einpunkt-Business-Case</p>
          </Link>
          <Link to="/app/trees" className="quick-card">
            <span className="quick-icon">DT</span>
            <strong>Driver-Tree bauen</strong>
            <p className="muted small">KPI-Ursache-Wirkung visualisieren</p>
          </Link>
          <Link to="/app/briefings" className="quick-card">
            <span className="quick-icon">BR</span>
            <strong>Briefing generieren</strong>
            <p className="muted small">CEO/CFO/COO Wochenreport</p>
          </Link>
          <Link to="/app/compliance" className="quick-card">
            <span className="quick-icon">FR</span>
            <strong>Compliance-Audit</strong>
            <p className="muted small">EU AI Act Art. 12 · 7y-Retention</p>
          </Link>
          <Link to="/app/candidates" className="quick-card">
            <span className="quick-icon">HL</span>
            <strong>HITL-Review</strong>
            <p className="muted small">KPI-Kandidaten approven · {d.open_candidates} offen</p>
          </Link>
        </div>
      </section>
    </section>
  );
}

function ActionGovernancePanel({
  queue,
  loading,
  error,
}: {
  queue?: AgentApprovalQueueReport;
  loading: boolean;
  error: unknown;
}) {
  const reviewNeeded = (queue?.pending_count ?? 0) > 0 || (queue?.expired_count ?? 0) > 0 || (queue?.missing_deadline_count ?? 0) > 0;
  const defaultDenyReady = queue?.default_deny_ready_count ?? 0;
  const items = queue?.items ?? [];
  return (
    <section className={`action-governance-panel ${reviewNeeded || defaultDenyReady > 0 ? "review" : "ready"}`}>
      <div className="section-head">
        <div>
          <p className="hero-eyebrow">Action Governance</p>
          <h2>Default-deny, Freigaben und offene Maßnahmen im Blick.</h2>
          <p className="muted small">
            Live aus <code>/v1/agent-runs/approval-queue</code>. Das Command Center zeigt keine externe Aktion als frei, solange HITL, Evidence oder Deadline fehlen.
          </p>
        </div>
        <span className={`status-pill ${loading ? "warning" : error ? "danger" : reviewNeeded || defaultDenyReady > 0 ? "warning" : "good"}`}>
          {loading ? "lade" : error ? "api error" : reviewNeeded || defaultDenyReady > 0 ? "review" : "ready"}
        </span>
      </div>

      <div className="action-governance-grid">
        <div><span>Approval Required</span><strong>{queue?.approval_required_count ?? "..."}</strong></div>
        <div><span>Pending</span><strong>{queue?.pending_count ?? "..."}</strong></div>
        <div><span>Expired</span><strong>{queue?.expired_count ?? "..."}</strong></div>
        <div><span>Default-deny ready</span><strong>{defaultDenyReady}</strong></div>
        <div><span>Missing deadline</span><strong>{queue?.missing_deadline_count ?? "..."}</strong></div>
        <div><span>Decided</span><strong>{queue?.decided_count ?? "..."}</strong></div>
      </div>

      <div className="action-governance-footer">
        <p className="muted small">
          {items.length > 0
            ? `${items.length} approval-required Runs brauchen operative Aufmerksamkeit.`
            : "Keine approval-required Runs in der aktuellen Queue; neue Maßnahmen erscheinen hier vor der Ausführung."}
        </p>
        <Link to="/app/agents" className="btn small">Approval Queue öffnen</Link>
      </div>

      {Boolean(error) && <p className="error">Approval Queue konnte im Command Center nicht geladen werden.</p>}
    </section>
  );
}

function auditRecordId(record: AuditRecord): string {
  return record.item_id ?? record.audit_id;
}

function auditItemText(record: AuditRecord, key: string): string | null {
  const value = record.item?.[key];
  if (value === null || value === undefined) return null;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  return null;
}

function evidenceLedgerRoute(record: AuditRecord): string {
  const id = auditRecordId(record);
  const runId = auditItemText(record, "run_id") ?? (record.kind === "agent_runs" ? id : null);
  const evidenceId = auditItemText(record, "evidence_id") ?? (record.kind === "evidence" ? id : null);
  const replayId = auditItemText(record, "replay_id") ?? (record.kind === "agent_replays" ? id : null);
  if (record.kind === "agent_replays" && runId && replayId) {
    return `/app/agents?run=${encodeURIComponent(runId)}&replay=${encodeURIComponent(replayId)}`;
  }
  if (record.kind === "evidence" && evidenceId) {
    return `/app/agents?evidence=${encodeURIComponent(evidenceId)}`;
  }
  if (runId) return `/app/agents?run=${encodeURIComponent(runId)}`;
  return `/app/agents?kind=${encodeURIComponent(record.kind)}&record=${encodeURIComponent(id)}`;
}

function formatLedgerTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? value : date.toLocaleString("de-DE");
}

function EvidenceLedgerPanel({
  records,
  loading,
  error,
}: {
  records: AuditRecord[];
  loading: boolean;
  error: unknown;
}) {
  return (
    <section className="evidence-ledger-panel">
      <div className="section-head">
        <div>
          <h2>Evidence Ledger</h2>
          <p className="muted small">Aktuelle Agent-, Replay- und Evidence-Records mit Hashkette und Tenant-Scope.</p>
        </div>
        <Link to="/app/agents" className="btn ghost small">Registry öffnen</Link>
      </div>
      {Boolean(error) && <p className="error">Evidence Ledger konnte nicht geladen werden.</p>}
      {loading && records.length === 0 && <p className="muted small">Lade Audit-Records ...</p>}
      {!loading && records.length === 0 && <p className="muted small">Noch keine Audit-Records sichtbar.</p>}
      <div className="evidence-ledger-list">
        {records.map((record) => {
          const id = auditRecordId(record);
          const agent = auditItemText(record, "agent");
          const status = auditItemText(record, "status") ?? record.action;
          const evidenceId = auditItemText(record, "evidence_id");
          const modelRoute = auditItemText(record, "model_route");
          const hash = record.hash.slice(0, 12);
          return (
            <Link key={`${record.audit_id}-${id}`} to={evidenceLedgerRoute(record)} className="evidence-ledger-row">
              <span className="ledger-kind">{record.kind}</span>
              <div>
                <strong>{agent ?? id}</strong>
                <p>{status} · {formatLedgerTime(record.ts)}</p>
                <code>{modelRoute ?? evidenceId ?? record.audit_id}</code>
              </div>
              <span className="ledger-hash">#{hash}</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function ReleaseReadinessPanel({
  trustGate,
  isolation,
  isolationLoading,
  isolationError,
  audit,
  auditLoading,
  auditError,
  isDemo,
}: {
  trustGate?: DataTrustGateStatus;
  isolation?: TenantIsolationStatus;
  isolationLoading: boolean;
  isolationError: unknown;
  audit?: AuditStatus;
  auditLoading: boolean;
  auditError: unknown;
  isDemo: boolean;
}) {
  const trustBlocking = !isDemo && Boolean(
    trustGate?.checks?.some((check) => check.release_blocking && ["fail", "blocked"].includes(check.status)),
  );
  const isolationBlocking = Boolean(
    isolation?.checks?.some((check) => check.release_blocking && ["fail", "not_configured"].includes(check.status)),
  );
  const auditBlocking = Boolean(audit && (!audit.enabled || !audit.tenant_scoped));
  const loading = isolationLoading || auditLoading;
  const error = isolationError || auditError;
  const status = trustBlocking || isolationBlocking || auditBlocking
    ? "fail"
    : error
      ? "warning"
      : loading
        ? "loading"
        : isolation?.status === "warning" || trustGate?.status === "needs_review"
          ? "warning"
          : "pass";
  const auditRecords = audit?.files.reduce((total, file) => total + (file.records ?? 0), 0) ?? 0;
  const actions = [
    ...(trustGate?.next_actions ?? []),
    ...((isolation?.checks ?? [])
      .filter((check) => check.release_blocking && check.status !== "pass")
      .map((check) => check.title) ?? []),
    ...(audit?.recent_errors ?? []),
  ].slice(0, 3);

  return (
    <section className={`release-readiness-panel status-${status}`}>
      <div className="section-head">
        <div>
          <h2>Release Readiness</h2>
          <p className="muted small">
            C-Level-Steuerung ist nur freigegeben, wenn Daten, Mandantengrenzen, Modellpolitik und Evidence nachvollziehbar sind.
          </p>
        </div>
        <span className={`status-pill ${status === "fail" ? "danger" : status === "pass" ? "good" : "warning"}`}>
          {loading ? "lade" : status}
        </span>
      </div>

      <div className="release-readiness-grid">
        <div>
          <span>Data Trust</span>
          <strong>{trustGate?.status ?? "offen"}</strong>
          <p>{isDemo ? "Sandbox markiert" : `${trustGate?.trusted_source_count ?? 0} trusted Quellen · ${trustGate?.metric_contract_count ?? 0} Contracts`}</p>
        </div>
        <div>
          <span>Tenant Isolation</span>
          <strong>{isolation?.status ?? (isolationLoading ? "lade" : "offen")}</strong>
          <p>{isolation ? `${isolation.checks?.length ?? 0} Checks · ${isolation.summary?.fail ?? 0} Fails` : "Mandantengate wird gelesen"}</p>
        </div>
        <div>
          <span>Audit Evidence</span>
          <strong>{audit?.enabled && audit.tenant_scoped ? "tenant-scoped" : auditLoading ? "lade" : "prüfen"}</strong>
          <p>{audit ? `${auditRecords.toLocaleString("de-DE")} Records · ${audit.files.length} Stores` : "Audit-Registry wird gelesen"}</p>
        </div>
        <div>
          <span>Model Policy</span>
          <strong>{isolation?.external_model_policy ?? "policy-gated"}</strong>
          <p>{isolation?.audit_namespace ?? "Audit-Namespace pending"}</p>
        </div>
      </div>

      {Boolean(error) && (
        <p className="error">Release-Gate konnte nicht vollständig geladen werden.</p>
      )}
      {actions.length > 0 && (
        <ul className="release-action-list">
          {actions.map((action) => <li key={action}>{action}</li>)}
        </ul>
      )}
      <div className="release-readiness-actions">
        <Link to="/app/compliance" className="btn small">Compliance öffnen</Link>
        <Link to="/app/agents" className="btn ghost small">Evidence prüfen</Link>
      </div>
    </section>
  );
}

function ProductJourneyPanel({
  onboardingProgress,
  trustGate,
  openCandidates,
  pendingBriefings,
  isDemo,
  hasTrustedData,
}: {
  onboardingProgress: number;
  trustGate?: DataTrustGateStatus;
  openCandidates: number;
  pendingBriefings: number;
  isDemo: boolean;
  hasTrustedData: boolean;
}) {
  const trustStatus = trustGate?.status ?? "blocked";
  const steps = [
    {
      no: "01",
      title: "Einführung",
      text: onboardingProgress >= 100 ? "Profil vollständig" : `${onboardingProgress} % Profilfortschritt`,
      state: onboardingProgress >= 100 ? "done" : "active",
      to: "/app/onboarding",
    },
    {
      no: "02",
      title: "Datenvertrauen",
      text: isDemo ? "Sandbox markiert" : hasTrustedData ? "Quality Gate bestanden" : trustGate?.next_actions?.[0] ?? "Quellen anschließen",
      state: isDemo || hasTrustedData ? "done" : trustStatus === "needs_review" ? "active" : "blocked",
      to: "/app/sources",
    },
    {
      no: "03",
      title: "Prozesslandschaft",
      text: "12 Phasen, 59 Teilprozesse, 26 Agenten",
      state: "ready",
      to: "/app/process-landscape",
    },
    {
      no: "04",
      title: "KPI-Review",
      text: openCandidates > 0 ? `${openCandidates} Kandidaten offen` : "Kandidaten nach Mapping",
      state: openCandidates > 0 ? "active" : "ready",
      to: "/app/candidates",
    },
    {
      no: "05",
      title: "ROI / Monte-Carlo",
      text: "P10/P50/P90 mit Replay und Evidence",
      state: "ready",
      to: "/app/roi",
    },
    {
      no: "06",
      title: "Briefings",
      text: pendingBriefings > 0 ? `${pendingBriefings} Briefings pending` : "C-Level Narrative erzeugen",
      state: pendingBriefings > 0 ? "active" : "ready",
      to: "/app/briefings",
    },
    {
      no: "07",
      title: "Governance",
      text: "Agent Runs, Replays, FRIA und Audit",
      state: "audit",
      to: "/app/agents",
    },
  ] as const;

  return (
    <section className="product-flow-panel" aria-label="KPI Enterprise Mining Produktstrecke">
      <div className="section-head">
        <div>
          <h2>Produktstrecke</h2>
          <p className="muted small">Geführter Ablauf vom ersten Mandantenprofil bis zu replaybarer C-Level-Evidence.</p>
        </div>
        <Link to="/app/process-landscape" className="btn ghost small">12-Phasen-Modell</Link>
      </div>
      <div className="product-flow-grid">
        {steps.map((step) => (
          <Link key={step.no} to={step.to} className={`product-flow-card state-${step.state}`}>
            <span className="flow-number">{step.no}</span>
            <strong>{step.title}</strong>
            <p>{step.text}</p>
            <em>{step.state}</em>
          </Link>
        ))}
      </div>
    </section>
  );
}

function DataTrustGatePanel({ gate }: { gate?: DataTrustGateStatus }) {
  if (!gate) return null;
  const titleByStatus: Record<DataTrustGateStatus["status"], string> = {
    demo: "Sandbox-Demo",
    trusted: "Data Trust Gate bestanden",
    needs_review: "Quality Review erforderlich",
    blocked: "Data Trust Gate blockiert",
  };
  const blockingCount = (gate.checks ?? []).filter((check) => check.release_blocking && ["fail", "blocked"].includes(check.status)).length;
  const actionRoute = gate.source_count > 0 ? "/app/sources" : "/app/onboarding";

  return (
    <section className={`data-trust-gate gate-${gate.status}`}>
      <div className="trust-gate-head">
        <div>
          <span className="trust-gate-label">Data-Trust-Gate<Hint term="trust_gate" /></span>
          <h2>{titleByStatus[gate.status]}</h2>
          <p>
            {gate.demo_mode
              ? "Demo-Werte bleiben sichtbar markiert. Echte Tenants brauchen Quellen, Quality Evidence und Metric Contracts."
              : `KPI-Anzeige ab Quality Score ${gate.quality_threshold}/100. ${blockingCount} release-blocking Checks offen.`}
          </p>
        </div>
        <Link to={actionRoute} className="btn small primary">
          Gate bearbeiten
        </Link>
      </div>

      <div className="trust-gate-summary">
        <div><strong>{gate.trusted_source_count}</strong><span>trusted Quellen</span></div>
        <div><strong>{gate.quality_report_count}</strong><span>Quality Reports</span></div>
        <div><strong>{gate.metric_contract_count}</strong><span>Metric Contracts</span></div>
        <div><strong>{gate.candidate_count}</strong><span>KPI-Kandidaten</span></div>
      </div>

      <p className="muted small trust-gate-why">
        Warum ist eine Quelle grün oder rot? Genau deshalb: Jede Prüfung unten wird bei jedem Aufruf neu
        ausgeführt. Prüfungen mit dem Vermerk „freigabe-relevant" blockieren die Anzeige von Kennzahlen,
        wenn sie fehlschlagen.
      </p>
      <ul className="trust-check-list">
        {(gate.checks ?? []).map((check) => (
          <li key={check.id} className={`trust-check ${check.status}`}>
            <span className="trust-check-status">{check.status}</span>
            <div>
              <strong>{check.title}</strong>
              <p>{check.detail}</p>
              <p className="trust-check-meta">
                {check.release_blocking ? "freigabe-relevant" : "informativ"}
                {check.evidence_refs && check.evidence_refs.length > 0
                  ? ` · ${check.evidence_refs.length} Beleg(e)`
                  : ""}
              </p>
            </div>
          </li>
        ))}
      </ul>

      {(gate.next_actions?.length ?? 0) > 0 && (
        <div className="trust-next">
          <strong>Nächster Schritt</strong>
          <span>{gate.next_actions?.[0]}</span>
        </div>
      )}
    </section>
  );
}

function SkeletonCenter() {
  return (
    <section className="cmd-center">
      <header className="cmd-hero">
        <div>
          <p className="hero-eyebrow">Command Center</p>
          <h1>Live-Übersicht</h1>
          <p className="hero-sub muted">Lade Daten …</p>
        </div>
      </header>
      <div className="kpi-grid">
        {[1,2,3,4].map((i) => <div key={i} className="kpi-card skeleton" />)}
      </div>
    </section>
  );
}
